import { useContext, useEffect, useState, useCallback } from 'react';
import FootballField from '@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/FootballField';
import Players from '@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Players';
import Bench from '@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Bench';
import MissingPlayers from '@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/MissingPlayers';
import PlayersInLines from './parts/PlayersInLines';
import Coaches from '@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Coaches/Coaches';
import { type IState, StateContext } from "../../../../state/stateContext";

interface ILineupWidgetProps {
    eventId: string | null;
    lineupData: any;
    isMVP?: boolean;
    homeTeamName?: string | null;
    awayTeamName?: string | null;
}

const LineupWidget = ({ lineupData, isMVP = true, homeTeamName, awayTeamName }: ILineupWidgetProps) => {
    const [homeFormation, setHomeFormation] = useState<string | null>(null)
    const [awayFormation, setAwayFormation] = useState<string | null>(null)
    const ctx = useContext(StateContext);
    const { setStContext } = ctx || {};

    const handleClickArea = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const isShirt = target.closest('[data-testid="one-player-shirt"]');
        const isTooltip = target.closest('[data-testid]')?.getAttribute('data-testid')?.match(/^player-tooltip-/);
        if (!isShirt && !isTooltip) {
            setStContext((prev: IState) => ({
                ...prev,
                activeTooltipPlayerId: null
            }));
        }
    };

    useEffect(() => {
        setHomeFormation(lineupData?.data?.homeFormation)
        setAwayFormation(lineupData?.data?.awayFormation)
    }, [lineupData])

    const getCoachesInfo = useCallback(() => {
        const homeCoach = lineupData?.data?.coaches?.home?.last_name ?
            lineupData.data.coaches.home.last_name : ``;
        const awayCoach = lineupData?.data?.coaches?.away?.last_name ?
            lineupData.data.coaches.away.last_name : ``;
        const hasHomeCoach = homeCoach && homeCoach.trim() !== '';
        const hasAwayCoach = awayCoach && awayCoach.trim() !== '';
        const hasAnyCoach = hasHomeCoach || hasAwayCoach;

        return {
            homeCoach,
            awayCoach,
            hasHomeCoach,
            hasAwayCoach,
            hasAnyCoach
        };
    }, [lineupData?.data?.coaches]);

    return (
        <div className='w-full' onClick={handleClickArea}>
            <FootballField 
                homeFormation={homeFormation} 
                awayFormation={awayFormation}
                homeFormationType={lineupData?.data?.homeFormationType}
                awayFormationType={lineupData?.data?.awayFormationType}
                homeLines={lineupData?.data?.homeLines}
                awayLines={lineupData?.data?.awayLines}
            >
                {lineupData?.data?.homeLines?.length > 0 && lineupData?.data?.awayLines?.length > 0 ?
                    (
                        <PlayersInLines
                            homeFormation={homeFormation}
                            awayFormation={awayFormation}
                            homeFormationType={lineupData?.data?.homeFormationType}
                            awayFormationType={lineupData?.data?.awayFormationType}
                            homeLines={lineupData?.data?.homeLines}
                            awayLines={lineupData?.data?.awayLines}
                        />
                    )
                    :
                    (
                        <Players
                            homeFormation={homeFormation}
                            awayFormation={awayFormation}
                            homePlayers={lineupData?.data?.homePlayers}
                            awayPlayers={lineupData?.data?.awayPlayers}
                            homeFormationType={lineupData?.data?.homeFormationType}
                            awayFormationType={lineupData?.data?.awayFormationType}
                        />
                    )
                }
            </FootballField>
            {(() => {
                const coachesInfo = getCoachesInfo();
                
                return (
                    <>
                        <Bench
                            homePlayers={lineupData?.data?.homePlayers}
                            awayPlayers={lineupData?.data?.awayPlayers}
                            homeTeamName={homeTeamName}
                            awayTeamName={awayTeamName}
                            isLastSection={isMVP && !coachesInfo.hasAnyCoach}
                        />
                        <Coaches
                            homeCoach={coachesInfo.homeCoach}
                            awayCoach={coachesInfo.awayCoach}
                            homeTeamName={homeTeamName}
                            awayTeamName={awayTeamName}
                            homeCountryCode={lineupData?.data?.coaches?.home?.country_icon}
                            awayCountryCode={lineupData?.data?.coaches?.away?.country_icon}
                            homeCard={lineupData?.data?.coaches?.home?.card}
                            awayCard={lineupData?.data?.coaches?.away?.card}
                        />
                        {!isMVP && <MissingPlayers
                            homePlayers={lineupData?.data?.homePlayers}
                            awayPlayers={lineupData?.data?.awayPlayers}
                            homeTeamName={homeTeamName}
                            awayTeamName={awayTeamName}
                        />
                        }
                    </>
                );
            })()}
        </div>
    )
}

export default LineupWidget;
